/**
 * Quantity Limiter Module
 *
 * Enforces purchase limits on product variants based on data attributes.
 * It tracks current cart quantities via AJAX updates and DOM scraping,
 * preventing users from exceeding product-specific or global quantity limits.
 */
(function() {
  // Global cache for tracking variant quantities currently in the cart
  window.cartVariantQuantities = {};
  
  // Flag to prevent infinite loops when programmatically updating input values
  let isUpdating = false;

  /**
   * Intercept AJAX fetch requests.
   * Detects cart actions (/cart/add, /cart/update, /cart/change, /cart/clear)
   * to trigger cart sync checks immediately after the cart updates.
   */
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    const urlString = typeof url === 'string' ? url : (url && url.url ? url.url : '');
    
    return originalFetch.apply(this, args).then(response => {
      const isCartAction = ['/cart/add', '/cart/update', '/cart/change', '/cart/clear'].some(path => 
        urlString.includes(path)
      );
      if (isCartAction) {
        setTimeout(debouncedCheck, 200);
      }
      return response;
    });
  };

  /**
   * Scrapes the DOM to get currently visible cart variant quantities.
   * Useful as a fallback/instant check before the AJAX cart endpoint resolves.
   * 
   * @returns {Object.<number, number>} Map of variant IDs to their respective quantities.
   */
  function getCartVariantQuantitiesFromDOM() {
    const quantities = {};
    const inputs = document.querySelectorAll('.wt-cart .js-counter-quantity[data-quantity-variant-id]');
    
    inputs.forEach(input => {
      const id = parseInt(input.getAttribute('data-quantity-variant-id'), 10);
      if (id && id > 100000) {
        quantities[id] = parseInt(input.value, 10) || 0;
      }
    });
    
    return quantities;
  }

  /**
   * Retrieves quantity limit value from the HTML attributes.
   * Checks current product-form or active product-form on page.
   * 
   * @param {HTMLElement|null} productForm - Optional product-form element to search limit from.
   * @returns {number} Allowed quantity limit (default is 10).
   */
  function getQuantityLimit(productForm = null) {
    const form = productForm || document.querySelector('product-form');
    if (form) {
      const parsed = parseInt(form.getAttribute('data-quantity-limit'), 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return 10; // Default limit
  }

  /**
   * Dynamic verification and limits calculation for the active product/variant.
   * Adjusts the product page input's 'max' attribute, and clips excess selected quantity.
   */
  function updateCartLimitBehavior() {
    const variantInput = document.querySelector('product-form input[name="id"]');
    if (!variantInput) return;
    const currentVariantId = parseInt(variantInput.value, 10);
    if (!currentVariantId) return;

    const productForm = variantInput.closest('product-form');
    const limit = getQuantityLimit(productForm);

    const cartQty = window.cartVariantQuantities[currentVariantId] || 0;
    const remainingAllowed = limit - cartQty;
      
    // Update maximum allowed quantity in the quantity selector input
    if (productForm) {
      const input = productForm.querySelector('.js-counter-quantity');
      if (input) {
        const maxLimit = remainingAllowed > 0 ? remainingAllowed : limit;
        input.setAttribute('max', maxLimit.toString());
        const currentVal = parseInt(input.value, 10) || 1;
        
        // If the user's current selection exceeds remaining allowed limits, reset it to the limit
        if (remainingAllowed > 0 && currentVal > remainingAllowed) {
          isUpdating = true;
          input.value = remainingAllowed;
          isUpdating = false;
          showQuantityLimitError(`Only ${limit} items can be added in cart.`);
        }
      }
    }
  }

  /**
   * Fetches latest cart quantities via Shopify /cart.js and runs verification check.
   * Updates window.cartVariantQuantities cache with absolute values from the backend.
   */
  function fetchCartAndCheck() {
    // Sync using current DOM quantities immediately to ensure instant UI feedback
    window.cartVariantQuantities = getCartVariantQuantitiesFromDOM();
    updateCartLimitBehavior();

    // Fetch full, official Shopify cart state asynchronously to ensure accuracy
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const quantities = {};
        cart.items.forEach(item => {
          quantities[item.variant_id] = item.quantity;
        });
        window.cartVariantQuantities = quantities;
        updateCartLimitBehavior();
      })
      .catch(err => console.error("[QtyLimiter] Error fetching cart:", err));
  }

  /**
   * Displays an error message on the product form container.
   * 
   * @param {string} msg - The error message string to display.
   */
  function showQuantityLimitError(msg) {
    const productForms = document.querySelectorAll('product-form');
    productForms.forEach(form => {
      if (typeof form.handleErrorMessage === 'function') {
        form.handleErrorMessage(msg);
      }
    });
  }

  /**
   * Binds initial quantity max limits to all input fields inside product forms.
   */
  function bindProductLimiters() {
    const productForms = document.querySelectorAll('product-form');
    productForms.forEach(form => {
      const input = form.querySelector('.js-counter-quantity');
      if (input) {
        const limit = getQuantityLimit(form);
        if (input.getAttribute('max') !== limit.toString()) {
          input.setAttribute('max', limit.toString());
        }
      }
    });
  }

  // Timer ID for debouncing cart validation checks
  let debounceTimeout;

  /**
   * Debounces cart checks to prevent duplicate execution during rapid DOM/AJAX updates.
   */
  function debouncedCheck() {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      fetchCartAndCheck();
    }, 100);
  }

  /**
   * Initializes a MutationObserver on the cart drawer element.
   * Detects visual changes to the drawer and triggers recalculation.
   */
  function initCartObserver() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        debouncedCheck();
      });
      observer.observe(cartDrawer, { childList: true, subtree: true, attributes: true });
    }
  }

  // Initialize event listeners when DOM content has finished loading
  document.addEventListener('DOMContentLoaded', () => {
    
    // 1. Capture phase click handler: intercepts button clicks before default handlers fire.
    // Handles clicks on quantity increment (+) and Add to Cart buttons.
    document.addEventListener('click', (event) => {
      const target = event.target;
      
      // Check if user clicked an active Add to Cart button
      const atcButton = target.closest('.js-add-to-cart, button[name="add"]');
      if (atcButton && !atcButton.hasAttribute('disabled') && atcButton.getAttribute('aria-disabled') !== 'true') {
        const variantInput = document.querySelector('product-form input[name="id"]');
        if (variantInput) {
          const currentVariantId = parseInt(variantInput.value, 10);
          if (currentVariantId) {
            const productForm = variantInput.closest('product-form');
            const limit = getQuantityLimit(productForm);
            const cartQty = window.cartVariantQuantities[currentVariantId] || 0;
            const remainingAllowed = limit - cartQty;
            
            // Block attempt if limit is already met/exceeded
            if (remainingAllowed <= 0) {
              event.stopPropagation();
              event.preventDefault();
              showQuantityLimitError("All " + limit + " Items are added in cart");
              return;
            }
            
            // Get currently selected quantity on product form input
            let selectedQty = 1;
            if (productForm) {
              const input = productForm.querySelector('.js-counter-quantity');
              if (input) {
                selectedQty = parseInt(input.value, 10) || 1;
              }
            }
            
            // Block attempt if selecting more than what remains of the limit
            if (selectedQty > remainingAllowed) {
              event.stopPropagation();
              event.preventDefault();
              showQuantityLimitError("Only " + limit + " items can be added in cart.");
              return;
            }
          }
        }
      }

      // Check if user clicked a quantity increase button (+)
      const isPlusBtn = target.closest('.js-counter-increase');
      if (isPlusBtn) {
        const container = target.closest('quantity-counter');
        if (container) {
          const input = container.querySelector('input');
          if (input) {
            const currentVal = parseInt(input.value, 10) || 0;
            const pForm = input.closest('product-form');
            if (pForm) {
              const variantInput = document.querySelector('product-form input[name="id"]');
              const currentVariantId = variantInput ? parseInt(variantInput.value, 10) : null;
              const cartQty = currentVariantId ? (window.cartVariantQuantities[currentVariantId] || 0) : 0;
              const limit = getQuantityLimit(pForm);
              const remainingAllowed = limit - cartQty;
              
              // Prevent clicking '+' if max quantity reached
              if (currentVal >= remainingAllowed || remainingAllowed <= 0) {
                console.log("[QtyLimiter] Enforcing max value limit. Blocking click.");
                event.stopPropagation();
                event.preventDefault();
                const msg = cartQty >= limit ? ("All " + limit + " Items are added in cart") : "Only " + limit + " items can be added in cart.";
                showQuantityLimitError(msg);
              }
            }
          }
        }
      }
    }, true); // Use capturing phase to run before standard theme events trigger additions

    // 2. Capture phase input/change handler to prevent users typing/scrolling past limits
    const enforceLimit = (event) => {
      if (isUpdating) return;
      const target = event.target;
      const isQtyInput = target.classList.contains('js-counter-quantity');
                         
      if (isQtyInput) {
        const val = parseInt(target.value, 10) || 0;
        
        const pForm = target.closest('product-form');
        if (pForm) {
          const variantInput = document.querySelector('product-form input[name="id"]');
          const currentVariantId = variantInput ? parseInt(variantInput.value, 10) : null;
          if (currentVariantId) {
            const cartQty = window.cartVariantQuantities[currentVariantId] || 0;
            const limit = getQuantityLimit(pForm);
            const remainingAllowed = limit - cartQty;
            
            if (remainingAllowed <= 0) {
              isUpdating = true;
              target.value = 1;
              target.dispatchEvent(new Event('change', { bubbles: true }));
              isUpdating = false;
              showQuantityLimitError("All " + limit + " Items are added in cart");
              return;
            }
            
            if (val > remainingAllowed) {
              isUpdating = true;
              target.value = remainingAllowed;
              target.dispatchEvent(new Event('change', { bubbles: true }));
              isUpdating = false;
              showQuantityLimitError("Only " + limit + " items can be added in cart.");
              return;
            }
          }
        }
      }
    };
    
    document.addEventListener('input', enforceLimit, true); // Capturing phase
    document.addEventListener('change', enforceLimit, true); // Capturing phase

    // 3. Listen for variant change input update
    document.addEventListener('change', (event) => {
      if (event.target.name === 'id') {
        setTimeout(updateCartLimitBehavior, 10);
      }
    });

    // Handle theme custom variant change end events
    document.addEventListener('variantChangeEnd', () => {
      setTimeout(updateCartLimitBehavior, 10);
    });

    // Subscribe to custom theme event systems (e.g., publish/subscribe events)
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(fetchCartAndCheck, 50);
      });
    }

    // Subscribe to cart drawer specific update events
    document.addEventListener('cart-drawer:refresh', () => {
      setTimeout(fetchCartAndCheck, 50);
    });

    // Initial setup tasks
    bindProductLimiters();
    initCartObserver();
    fetchCartAndCheck();
  });
})();
